import { Alert, Button, ButtonGroup, Col, FormCheck, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import CustomerSelect from "../../../components/Custom/Main/Customer/CustomerSelect";
import TextArea from "../../../components/Forms/Element/TextArea";
import ProductSelect from "../../../components/Custom/Main/Product/ProductSelect";
import ProductThumbnail from "../../../components/Custom/Main/Product/ProductThumbnail";
import PlusIcon from "../../../components/Icon/PlusIcon";
import InputDecimal from "../../../components/Forms/Element/InputDecimal";
import axiosInstance from "../../../../services/AxiosInstance";
import Api from "../../../../constants/Api";
import { convertDoubleToStringPTBR, convertQuantityToStringPTBR, convertStringToStringDouble } from "../../../../utils/ConverterUtils";
import { warnAlert } from "../../../components/alert/defaultAlert";
import ModalPage from "../../../components/Common/Modal/ModalPage";
import { formatNumberWithTwoDecimalPlaces } from "../../../../utils/FormatUtils";
import EmployeeSelect from "../../../components/Custom/Main/Employee/EmployeeSelect";
import { useParams } from "react-router-dom";
import { ApiError } from "../../../components/Common/ApiError";
import LoadingIcon from "../../../components/Icon/LoadingIcon";
import PlanDetail from "../../../components/Custom/Subscription/Plan/PlanDetail";
import QuantityInput from "../../../components/Forms/Element/QuantityInput";
import BranchSelect from "../../../components/Custom/Main/Branch/BranchSelect";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import { useBranch } from "../../../../context/BranchContext";
import { isBeforeToday } from "./appointmentSaleDate";
import ClubCrownIcon from "../../../components/Icon/ClubCrownIcon";
import ClubLateIcon from "../../../components/Icon/ClubLateIcon";
import CustomerLatePaymentAlert from "../../../components/Custom/Main/Customer/CustomerLatePaymentAlert";
import PlanOffDayNotice from "../../../components/Custom/Subscription/Plan/PlanOffDayNotice";
import { toWorkDayFromInput } from "../../../../utils/WorkDayUtils";
import { formatDateTimeLocalInput } from "../../../../utils/DateUtils";
import FormSection from "../../../components/Common/Form/FormSection";
import OrderStockNotice from "./OrderStockNotice";
import { ProductStockStatus, fetchProductStock } from "../../../../utils/ProductStockUtils";
import { OrderDiscountType, computeOrderDiscount } from "../../../../utils/OrderDiscountUtils";
import SegmentedButtons from "../../../components/Forms/Element/SegmentedButtons";
import { eligiblePlanProduct } from "./planBenefitPreview";

const Form = (props) => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const { appointmentUuid } = useParams();
    const { formik, setParentItems, resetForm, setOrderTotalValue, setOrderDiscount, setParentAppointmentId, onOpenQuickCustomer, setPlanLookupState } = props;
    const [items, setItems] = useState([]);
    // Último cliente cuja assinatura foi consultada, para distinguir troca de cliente de troca de data.
    const lookedUpCustomerId = useRef(null);
    const [price, setPrice] = useState("0,00");
    const [product, setProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const [summaryTotal, setSummaryTotal] = useState(0);
    // Carregamento dos dados que pré-preenchem a venda vinda do agendamento.
    const [showOrderDataLoading, setShowOrderDataLoading] = useState(false);
    const [employeeFieldDisabled, setEmployeeFieldDisabled] = useState(false);
    const [subscription, setSubscription] = useState(null); // Dados da assinatura
    const [requestDateLocked, setRequestDateLocked] = useState(false); // Venda de atendimento de dia anterior
    const [showSubscriptionDetail, setShowSubscriptionDetail] = useState(false);
    const [unit, setUnit] = useState('UNIT');
    const [disableAddButton, setDisableAddButton] = useState(false);
    const [barcode, setBarcode] = useState('');
    // Mantém a escolha do tipo próxima à inclusão do item, reduzindo a lista exibida ao operador.
    const [selectedProductType, setSelectedProductType] = useState(null);


    // Controla a exibição dos campos
    const [hiddenCustomer, setHiddenCustomer] = useState(false);

    /**
     * Busca o preço do produto.
     */
    function findProductDataById(id) {
        setDisableAddButton(true);
        axiosInstance.get(Api.main.product + "/" + id)
            .then((response) => {
                let product = response.data;
                setPrice(formatNumberWithTwoDecimalPlaces(product.price));
                setUnit(product.unit);
            })
            .finally(() => setDisableAddButton(false));
    }

    /**
     * Busca produto por código de barras.
     */
    function findProductByBarcode(code) {
        if (!code || code.trim() === '') return;
        
        setDisableAddButton(true);
        axiosInstance.get(Api.main.product + "/barcode/" + code)
            .then((response) => {
                let productData = response.data;
                setPrice(formatNumberWithTwoDecimalPlaces(productData.price));
                setUnit(productData.unit);
                
                // Atualiza o select com o produto encontrado
                setProduct({
                    value: productData.id,
                    label: productData.name
                });
            })
            .catch((error) => {
                warnAlert(t('validation.custom.product_not_found_by_barcode'));
                setBarcode('');
            })
            .finally(() => setDisableAddButton(false));
    }

    /**
     * Handler para o input de código de barras (Enter).
     */
    function handleBarcodeKeyPress(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            findProductByBarcode(barcode);
        }
    }

    function getSelectedCustomer() {
        return formik.values.customer !== undefined && formik.values.customer !== null ? formik.values.customer.value : null;
    }

    // Serviço não tem código de barras, então o campo de leitura só faz sentido
    // quando produtos podem aparecer na busca.
    const showBarcodeField = selectedProductType !== 'SERVICE';

    const onProductTypeChange = (productType) => {
        setSelectedProductType(productType);
        // Evita adicionar por engano um item selecionado antes da troca de filtro.
        setProduct(null);
        setPrice('0,00');
        setQty(1);
        setUnit('UNIT');
        // Some com o que estava digitado ao esconder o campo, para não sobrar
        // um código pendurado ao voltar o filtro para produtos.
        setBarcode('');
    };

    /**
     * Confere o saldo antes de deixar o item entrar na venda.
     *
     * O backend continua validando na gravação, que é a fonte da verdade contra
     * duas vendas simultâneas do mesmo item. Aqui a checagem existe só para o
     * operador descobrir a falta no momento de adicionar, e não depois de montar
     * a venda inteira e tentar confirmar.
     *
     * Retorna true quando o item pode ser adicionado.
     */
    async function hasStockToAdd() {
        const { status, qty: balance } = await fetchProductStock(product.value, formik.values.branch?.value);

        // Sem controle de estoque, sem filial escolhida ou consulta falha: não barra.
        // Barrar aqui inventaria uma falta que o backend não confirma.
        if (status !== ProductStockStatus.EMPTY && status !== ProductStockStatus.AVAILABLE) {
            return true;
        }

        const wanted = parseFloat(convertStringToStringDouble(String(qty)));
        if (status === ProductStockStatus.AVAILABLE && balance >= wanted) {
            return true;
        }

        warnAlert(t('validation.custom.stock_insufficient', {
            product: product.label,
            balance: status === ProductStockStatus.EMPTY ? 0 : balance
        }));
        return false;
    }

    /**
     * Adiciona um item na estrutura da venda.
     */
    async function onAdd() {
        if (!product) {
            warnAlert(t('validation.required.product'));
        } else if (!qty) {
            warnAlert(t('validation.required.qty'));
        } else if (!price) {
            warnAlert(t('validation.required.price'));
        } else {
            let item = createItem(null);

            // Verifica se o item já existe na lista. Só é permitido
            // adiciona um novo item caso ele não exista na lista.
            let add = true;
            for (var i = 0; i < items.length; i++) {
                if (items[i].product.id === item.product.id) {
                    warnAlert(t("validation.custom.product_already_added", { product: item.product.name }));
                    add = false;
                    break;
                }
            }

            if (add) {
                // A consulta só acontece depois do teste de duplicado, para não
                // chamar a api à toa quando o item já está na lista.
                setDisableAddButton(true);
                try {
                    add = await hasStockToAdd();
                } finally {
                    setDisableAddButton(false);
                }
            }

            // Adiciona um novo item a lista.
            if (add) {
                // INFO(Devs): originalPrice é o preço CHEIO enviado ao backend, que é a fonte única
                // do desconto do plano. Aqui o desconto é só uma prévia para exibição/checkout;
                // o valor gravado/cobrado é recalculado uma única vez no backend (sem dupla aplicação).
                item.originalPrice = parseFloat(convertStringToStringDouble(String(item.price)));
                applyPlanBenefitToItem(item);

                // Coloca o preço no padrão da linguagem. Recebe 2,00 e converte para 2.00
                item.qty = convertStringToStringDouble(item.qty);

                let localItems = [...items, item];
                setItems(localItems);
                setParentItems(localItems);

                // Limpa os campos.
                setProduct(null);
                setPrice("0,00");
                setQty(1);

            }
        }
    }

    /**
     * Remove o item da lista.
     */
    function onRemove(id) {
        // Copia o array para uma variável local.
        let arr = [...items];

        for (var i = 0; i < arr.length; i++) {
            if (arr[i].product.id === id) {
                arr.splice(i, 1);
                break;
            }
        }

        setItems(arr);
        setParentItems(arr);
    }

    /**
     * Adiciona os itens da venda.
     */
    const createItem = (apiItem) => {
        let item = null;

        if (apiItem !== null) {
            item = {
                product: {
                    id: apiItem.product.id,
                    name: apiItem.product.name,
                    uploadedFileLocation: apiItem.product.uploadedFileLocation
                },
                qty: apiItem.qty,
                price: apiItem.price,
                // originalPrice = preço cheio enviado ao backend (fonte única do desconto).
                originalPrice: apiItem.originalPrice != null ? apiItem.originalPrice : apiItem.price,
                total: apiItem.qty * apiItem.price,
                isSubscription: apiItem.isSubscription || false,
                // Preço já processado pelo backend (edição): originalPrice pode já vir
                // com o benefício aplicado, então reaplicar o desconto aqui cobraria duas vezes.
                planPriceLocked: true
            }
        } else {
            item = {
                product: {
                    id: product.value,
                    name: product.label,
                    uploadedFileLocation: product?.uploadedFileLocation
                },
                qty: convertStringToStringDouble(qty),
                price: price,
                originalPrice: parseFloat(convertStringToStringDouble(String(price))),
                total: convertStringToStringDouble(qty) * price,
                isSubscription: false
            }
        }

        return item;
    }

    // Desconto da venda calculado sobre a soma dos itens, que já estão com o benefício do plano
    // aplicado. Recalcula a cada mudança de item, de tipo ou de número digitado: um desconto em
    // reais válido pode deixar de ser quando um item é removido.
    const manualDiscountInputValue = parseFloat(convertStringToStringDouble(formik.values.manualDiscountInput)) || 0;
    const orderDiscount = computeOrderDiscount(summaryTotal, formik.values.manualDiscountType, manualDiscountInputValue);
    const orderNetTotal = Math.round((summaryTotal - orderDiscount.value) * 100) / 100;

    useEffect(() => {
        let arr = [...items];
        let total = 0;
        for (var i = 0; i < arr.length; i++) {
            total += arr[i].total;
        }

        setSummaryTotal(total);
    }, [items]);

    // Envia para o pai o valor a receber, já com o desconto, e o próprio desconto, que o pai usa
    // no modal de pagamento e para barrar a confirmação quando ele é inválido.
    useEffect(() => {
        setOrderTotalValue(orderNetTotal);
        if (setOrderDiscount) {
            setOrderDiscount(orderDiscount);
        }
        // orderDiscount é recriado a cada render; as dependências são os valores que o formam.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderNetTotal, orderDiscount.value, orderDiscount.error]);

    useEffect(() => {
        setItems([]);
        setParentItems([]);
        setSummaryTotal(0);
        setOrderTotalValue(0);
    }, [resetForm]);

    // Appointment
    // INFO(Devs): Quando carregado via botão "Gerar Venda" no agendamento, busca 
    // os detalhes do agendamento para pré-preencher o carrinho (Form.js).
    useEffect(() => {
        if (appointmentUuid) {
            setHiddenCustomer(true);

            setShowOrderDataLoading(true);
            axiosInstance.get(Api.appointment.byUuid + "/" + appointmentUuid)
                .then(resp => {
                    let data = resp.data;
                    if (data !== null) {
                        // Set the parent appointment ID
                        // INFO(Devs): Envia o ID para o OrderNew.js vincular ao payload final da Venda.
                        if (setParentAppointmentId) {
                            setParentAppointmentId(data.id);
                        }

                        // Comanda fechada depois do dia do atendimento (a recepção saiu cedo e fechou
                        // no dia seguinte): a data da venda passa a ser a do atendimento, para ele
                        // contar no dia, e no mês, em que aconteceu. Antes ficava a hora do fechamento,
                        // e os atendimentos de sábado 31/10/2026 da Meu Patrão fechados em 03/11 foram
                        // parar em novembro (atendimentos, comissão e Pote). No mesmo dia continua a
                        // hora do fechamento, como sempre foi. O campo fica travado porque o servidor
                        // grava a data do atendimento de qualquer forma (OrderService#applyAppointmentDate).
                        if (data.scheduledDate) {
                            const scheduledLocal = String(data.scheduledDate).slice(0, 16);
                            if (isBeforeToday(scheduledLocal)) {
                                formik.setFieldValue('requestDate', scheduledLocal);
                                setRequestDateLocked(true);
                            }
                        }

                        // Populate Employee
                        // INFO(Devs): Auto-preenche o funcionário responsável pelo agendamento.
                        if (data.employee != null) {
                            formik.setFieldValue('employee', {
                                value: data.employee.id,
                                label: data.employee.fullName
                            });
                            setEmployeeFieldDisabled(true); // Can lock it if desired
                        }

                        // Populate Customer
                        if (data.customer != null) {
                            formik.setFieldValue('customer', {
                                value: data.customer.id,
                                label: data.customer.fullName,
                                customer: data.customer
                            });
                        }

                        // Populate Branch
                        // INFO(Devs): Pré-preenche a filial com a do próprio agendamento.
                        // Quando há filial global selecionada (selectedBranch), o campo já vem
                        // travado nela (isDisabled), então não sobrescrevemos esse contexto.
                        if (data.branch != null && !selectedBranch) {
                            formik.setFieldValue('branch', {
                                value: data.branch.id,
                                label: data.branch.name
                            });
                        }

                        // Populate Services as OrderItems
                        // INFO(Devs): Um agendamento pode ter vários serviços. Quando houver mais de um,
                        // semeia um item por serviço (com o preço snapshot de cada). Caso contrário,
                        // mantém o comportamento de serviço único (importante para plano de assinatura).
                        if (data.serviceItems != null && data.serviceItems.length > 1) {
                            const isClub = !!data.customer?.clubMember;
                            const orderItems = data.serviceItems
                                .filter(si => si.service != null)
                                .map(si => {
                                    // Preço acertado no agendamento, já com o benefício do plano
                                    // aplicado por serviço (ver AppointmentService#applyPlanBenefit).
                                    const price = si.price != null ? si.price : (si.service.price != null ? si.service.price : 0);
                                    // O backend da venda é a fonte única do desconto e recalcula a
                                    // partir do preço enviado. Mandar o preço já descontado faria o
                                    // desconto ser aplicado duas vezes num plano de desconto parcial
                                    // (100 vira 70 no agendamento e 49 na venda). Por isso o campo
                                    // enviado é o preço cheio do serviço, e o descontado fica só na
                                    // exibição do carrinho.
                                    const fullPrice = si.service.price != null ? si.service.price : price;
                                    return {
                                        product: { id: si.service.id, name: si.service.name },
                                        qty: 1,
                                        price,
                                        originalPrice: fullPrice,
                                        isSubscription: isClub && price < fullPrice,
                                        total: price
                                    };
                                });
                            setItems(orderItems);
                            setParentItems(orderItems);
                        } else if (data.service != null) {
                            let isSubscriptionItem = false;
                            // Se o preço for 0 e o cliente for do clube, consideramos que é do clube
                            // pois o backend já processou o desconto na hora de agendar.
                            if (data.price === 0 && data.customer?.clubMember) {
                                isSubscriptionItem = true;
                            }
                            
                            const servicePrice = data.price != null ? data.price : data.service.price;
                            let item = {
                                product: {
                                    id: data.service.id,
                                    name: data.service.name
                                },
                                qty: 1,
                                price: servicePrice,
                                // Agendamentos antigos podem ter gravado o preço cheio. A
                                // assinatura carregada abaixo recalcula a prévia a partir do
                                // preço atual do cadastro do serviço.
                                originalPrice: data.service.price != null ? data.service.price : servicePrice,
                                isSubscription: isSubscriptionItem
                            };
                            item.total = item.qty * item.price;
                            setItems([item]);
                            setParentItems([item]);
                        }
                    }
                })
                .catch(error => ApiError(error))
                .finally(() => setShowOrderDataLoading(false));
        }
    }, [appointmentUuid]);

    // A prévia usa a elegibilidade já calculada no servidor para este cliente e data.
    function getPlanProductForItem(item) {
        return eligiblePlanProduct(subscription, item.product.id);
    }

    /**
     * A data da venda cai num dia em que o plano dá benefício integral?
     *
     * Plano sem dias configurados vale a semana toda, mesma leitura do backend: vazio nunca
     * significa "nenhum dia", senão todo assinante antigo perderia o benefício.
     */
    function isPlanBenefitDay() {
        const benefitDays = subscription?.plan?.benefitDays || [];
        if (benefitDays.length === 0) {
            return true;
        }

        const workDay = formik.values.requestDate
            ? toWorkDayFromInput(formik.values.requestDate)
            : toWorkDayFromInput(new Date());
        return workDay == null || benefitDays.includes(workDay);
    }

    /**
     * Aplica no item a prévia do benefício do plano do cliente, sempre recalculando a partir do
     * preço cheio (originalPrice) para o resultado não depender de quantas vezes rodou.
     *
     * INFO(Devs): o critério é o MESMO do backend (PlanDiscountResolver):
     * 1. o produto está no plano;
     * 2. o desconto cadastrado é maior que zero. Antes, a tela tratava desconto zero como
     *    "incluso no plano" e zerava o item, enquanto o servidor cobrava o valor cheio na
     *    gravação. Para um item sair de graça, o plano precisa ter 100% de desconto;
     * 3. o DIA da venda está entre os dias de benefício do plano. Fora deles vale o desconto
     *    reduzido (offDayDiscountPercentage) e o item não conta como uso do plano. Sem esta
     *    regra a tela zerava o item numa segunda-feira que o plano não cobre, e o servidor
     *    gravava o valor com 10% — exatamente a divergência descrita abaixo.
     *
     * Duas coisas NÃO entram nessa lista, e nos dois casos porque o servidor também não as
     * aplica no balcão: atraso na mensalidade (assinante inadimplente continua com o
     * benefício, a cobrança é papel da régua de cobrança) e o limite de usos do plano (venda
     * lançada pela recepção não é barrada por cota).
     *
     * Quando a tela e o servidor divergem, a divergência aparece sempre do mesmo jeito: o
     * modal de pagamento exibe um total e a venda registra outro.
     *
     * Muta o item recebido, seguindo o padrão já usado no onAdd.
     */
    function applyPlanBenefitToItem(item) {
        const originalPrice = parseFloat(item.originalPrice != null ? item.originalPrice : item.price) || 0;
        // A elegibilidade vem do servidor, que também decide o preço ao salvar a venda.
        const planProduct = getPlanProductForItem(item);

        // Produto fora do plano é valor cheio em qualquer dia: o desconto de dia fora reduz o
        // benefício do plano, não é desconto geral da casa.
        if (!planProduct) {
            item.price = originalPrice;
            item.total = (parseFloat(convertStringToStringDouble(String(item.qty))) || 0) * originalPrice;
            item.isSubscription = false;
            return item;
        }

        const coveredDay = isPlanBenefitDay();
        const discount = coveredDay || formik.values.offDayDiscountOverride
            ? (planProduct.discountPercentage || 0)
            : (subscription?.plan?.offDayDiscountPercentage || 0);
        const hasDiscount = discount > 0;

        item.price = hasDiscount ? originalPrice * (1 - discount / 100) : originalPrice;
        item.total = (parseFloat(convertStringToStringDouble(String(item.qty))) || 0) * item.price;
        // isSubscription significa "consumiu cota": só o benefício do dia coberto consome.
        item.isSubscription = hasDiscount && coveredDay;

        return item;
    }

    /**
     * Reaplica o benefício do plano nos itens já lançados sempre que a assinatura do cliente
     * muda (inclusive quando ela chega depois, na resposta da API).
     *
     * Sem isto, item adicionado ANTES de escolher o cliente ficava com o preço cheio na tela:
     * o benefício só era calculado no momento do onAdd e nada revisitava a lista quando a
     * assinatura carregava. Como o backend recalcula o desconto na gravação, a venda registrada
     * saía menor do que o valor exibido no modal de pagamento e efetivamente cobrado do cliente.
     *
     * Itens com planPriceLocked ficam de fora: vendas em edição já chegam com o
     * preço acertado. Itens de agendamento não são travados, pois dados antigos podem estar
     * gravados pelo preço cheio e precisam ser recalculados ao abrir esta tela.
     */
    useEffect(() => {
        if (items.length === 0) {
            return;
        }

        let changed = false;

        const recalculatedItems = items.map((item) => {
            if (item.planPriceLocked) {
                return item;
            }

            const recalculatedItem = applyPlanBenefitToItem({ ...item });

            if (recalculatedItem.price !== item.price || recalculatedItem.isSubscription !== item.isSubscription) {
                changed = true;
            }

            return recalculatedItem;
        });

        // Sem alteração real, não mexe no estado: trocar a referência da lista à toa dispararia
        // de novo o cálculo do total e o reenvio dos itens ao componente pai.
        if (!changed) {
            return;
        }

        setItems(recalculatedItems);
        setParentItems(recalculatedItems);
        // Assinatura e data da venda disparam o recálculo; incluir "items" aqui criaria laço
        // com o próprio setItems acima. A data entra porque o benefício depende do dia da
        // semana: mudar a venda de sexta para segunda muda o preço dos itens do plano.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscription, formik.values.requestDate, formik.values.offDayDiscountOverride]);

    useEffect(() => {
        // Sem cliente selecionado não há plano a aplicar. Antes faltava este caminho: ao limpar
        // ou trocar o cliente, a assinatura do anterior continuava no estado e seguia valendo.
        if (!formik.values.customer) {
            setSubscription(null);
            setPlanLookupState('ready');
            // Reselecionar o mesmo cliente depois de limpar volta a ser uma troca de cliente.
            lookedUpCustomerId.current = null;
            return;
        }

        let active = true;
        // Não permite cobrar enquanto o preço do plano ainda está sendo consultado.
        setPlanLookupState('loading');
        // Só zera a assinatura quando o cliente mudou: o plano de outro cliente não pode
        // descontar estes itens nem por um instante. Na troca apenas de data, o cliente é o
        // mesmo e a assinatura anterior fica na tela até a nova resposta chegar, o que evita o
        // preço dos itens piscar entre cheio e com desconto. O fechamento continua barrado
        // pelo estado 'loading' durante a consulta.
        if (lookedUpCustomerId.current !== formik.values.customer.value) {
            setSubscription(null);
        }
        lookedUpCustomerId.current = formik.values.customer.value;
        const referenceDate = String(formik.values.requestDate || '').slice(0, 10);
        axiosInstance.get(Api.subscription.subscription + "/customer/id/" + formik.values.customer.value,
            { params: referenceDate ? { referenceDate } : {} })
            .then(resp => { if (active) { setSubscription(resp.data || null); setPlanLookupState('ready'); } })
            // 404 é o cliente sem assinatura ativa. Qualquer outra falha também precisa zerar,
            // senão o plano do cliente anterior continuaria descontando os itens deste.
            .catch(error => { if (active) { setSubscription(null); setPlanLookupState(error?.response?.status === 404 ? 'ready' : 'error'); } });
        return () => { active = false; };
        // Chave pelo id do cliente e pela data da venda: atualizar um dado do mesmo cliente (o
        // clubLate depois da baixa por fora) não pode buscar a assinatura de novo, porque isso
        // recalcula os preços dos itens. A data entra porque a elegibilidade é apurada no dia da
        // venda, e lançamento retroativo pode ter resultado diferente do de hoje.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.customer?.value, formik.values.requestDate]);

    return (
        <>
            <ModalPage
                title={t('subscription_details')}
                show={showSubscriptionDetail}
                setShow={setShowSubscriptionDetail}
                child={<PlanDetail entity={subscription} />}
                size="md"
                centered={true}
            />

            {
                (showOrderDataLoading) ?
                    <Alert variant="warning" show={showOrderDataLoading}>
                        <LoadingIcon />&nbsp;
                        {t('message.looking_order_data')}.
                    </Alert>
                    :
                    <></>
            }

            {
                (subscription) ? <>
                    <div className="alert alert-info shadow-sm border-0 d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3" role="alert" style={{background: 'linear-gradient(135deg, #145650 0%, #0a3330 100%)', color: 'white'}}>
                        <div className="d-flex align-items-center">
                            <i className="fa-solid fa-star fa-2x me-3"></i>
                            <div>
                                <h5 className="mb-0" style={{color: 'white'}}>
                                    {subscription.pendingFirstPayment
                                        ? t('plan_awaiting_first_payment')
                                        : t('customer_with_subscription')}
                                </h5>
                                <small style={{color: 'rgba(255,255,255,0.8)'}}>{subscription.pendingFirstPayment
                                    ? t('plan_benefit_pending_first_payment')
                                    : subscription.planBenefitEligible ? t('subscription_benefits_applied') : t('plan_benefit_unavailable')}</small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-light btn-sm"
                            onClick={() => setShowSubscriptionDetail(true)}
                            style={{fontWeight: '600'}}
                        >
                            <i className="fas fa-eye me-2"></i>
                            {t('see_plan')}
                        </button>
                    </div>
                    </> 
                    :
                    <></>
            }

            {/** Alerta de mensalidade atrasada */}
            {formik.values.customer?.customer && (
                <CustomerLatePaymentAlert
                    customer={formik.values.customer.customer}
                    // Pagou no balcão e ficou em dia: tira o ícone de atraso e o aviso de atraso
                    // ao fechar a venda como Assinatura (OrderNew lê este mesmo clubLate).
                    onRegularized={() => formik.setFieldValue('customer.customer.clubLate', false)}
                />
            )}

            {/* Preço do clube no dia da venda: sem serviço específico, o aviso vale pelo plano.
                A venda usa a data da requisição, a mesma que o backend usa para precificar. */}
            {formik.values.customer?.value && (
                <PlanOffDayNotice
                    customerId={formik.values.customer.value}
                    date={formik.values.requestDate || new Date()}
                />
            )}

            {/* A explicação do dia vem antes da decisão manual, para o operador entender a
                diferença de preço antes de liberar a exceção. */}
            {subscription?.planBenefitEligible === true && !isPlanBenefitDay() && (
                <div className="sales-order-form__exception-card">
                    <h6 className="mb-2 d-flex align-items-center gap-2">
                        <i className="fa-solid fa-circle-info"></i>
                        Exceção do benefício
                    </h6>
                    <small className="text-muted d-block mb-2">Use somente quando este cliente estiver autorizado a utilizar o desconto fora dos dias previstos no plano.</small>
                    <FormCheck>
                        <input className="form-check-input" type="checkbox" id="off-day-discount-override"
                            checked={Boolean(formik.values.offDayDiscountOverride)}
                            onChange={(event) => formik.setFieldValue('offDayDiscountOverride', event.target.checked)} />
                        <FormLabel htmlFor="off-day-discount-override" className="form-check-label">
                            Liberar desconto excepcional fora do dia do plano
                        </FormLabel>
                    </FormCheck>
                </div>
            )}

            <Row className="sales-order-form mb-4">
                <Col className="col-md-8">
                    {
                        (appointmentUuid !== undefined && formik.values.customer) ?
                            <div className="alert alert-info shadow-sm border-0 d-flex align-items-center" role="alert">
                                <i className="fas fa-calendar-check fa-2x me-3"></i>
                                <div>
                                    <h4 className="mb-0 d-flex align-items-center">
                                        {formik.values.customer.customer?.fullName || formik.values.customer.label}
                                        {formik.values.customer.customer?.clubMember && (
                                            <span className="ms-2"><ClubCrownIcon /></span>
                                        )}
                                        {formik.values.customer.customer?.clubLate && (
                                            <span className="ms-2"><ClubLateIcon /></span>
                                        )}
                                    </h4>
                                    <small className="text-muted">Venda originada de Agendamento</small>
                                </div>
                            </div>
                            :
                            <></>
                    }
                    <div className="form-layout form-layout--spaced">
                        <FormSection
                            title={t('basic_information')}
                            description={t('order_basic_information_hint')}
                            icon="fa-solid fa-receipt"
                        >

                            <Row className="g-3 mb-0">
                                <Col md={6}>
                                    <FormLabel>{t('order_launch_date')} <AsteriskRequired /></FormLabel>
                                    <input
                                        type="datetime-local"
                                        name="requestDate"
                                        className={`form-control ${formik.touched.requestDate && formik.errors.requestDate ? 'is-invalid' : ''}`}
                                        value={formik.values.requestDate || ''}
                                        max={formatDateTimeLocalInput()}
                                        disabled={requestDateLocked}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <small className="text-muted d-block mt-2">
                                        {t(requestDateLocked ? 'order_launch_date_appointment_hint' : 'order_launch_date_hint')}
                                    </small>
                                    {
                                        formik.touched.requestDate && formik.errors.requestDate ?
                                            <div className="invalid-feedback d-block">{formik.errors.requestDate}</div>
                                            :
                                            <></>
                                    }
                                </Col>
                            </Row>
                            <Row className="g-3 mt-0" hidden={hiddenCustomer}>
                                <Col md={12}>
                                    <FormGroup>
                                        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                                            <FormLabel className="mb-0">{t('customer')}</FormLabel>
                                            {onOpenQuickCustomer && (
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm p-0"
                                                    onClick={onOpenQuickCustomer}
                                                >
                                                    <i className="fas fa-plus me-1"></i>
                                                    Novo cliente
                                                </button>
                                            )}
                                        </div>
                                        <CustomerSelect value={formik.values.customer}
                                            onChange={(value) => {
                                                formik.setFieldValue('customer', value);
                                            }}
                                            onBlur={formik.handleBlur} />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row className="g-3 mt-0">
                                <Col md={6}>
                                    <FormLabel>{t('branch')} <AsteriskRequired /></FormLabel>
                                    <BranchSelect
                                        name="branch"
                                        value={formik.values.branch}
                                        onChange={(value) => {
                                            formik.setFieldValue('branch', value);

                                            // Trocar a filial invalida quem já estava escolhido: o
                                            // colaborador pode não atender na nova unidade e sairia
                                            // da lista. Só limpa na troca feita pelo usuário; o
                                            // preenchimento automático (agendamento) usa
                                            // setFieldValue direto e não passa por aqui.
                                            if (formik.values.employee) {
                                                formik.setFieldValue('employee', null);
                                            }
                                        }}
                                        error={formik.errors.branch}
                                        onBlur={formik.handleBlur}
                                        isDisabled={!!selectedBranch}
                                    />
                                </Col>
                                <Col md={6}>
                                    <FormLabel>{t('employee')}</FormLabel>
                                    <EmployeeSelect
                                        name="employee"
                                        onChange={(value) =>
                                            formik.setFieldValue('employee', value)
                                        }
                                        value={formik.values.employee}
                                        onBlur={formik.handleBlur}
                                        error={formik.errors.employee}
                                        isDisabled={employeeFieldDisabled}
                                        // Segue a filial escolhida no formulário, e não a do contexto
                                        // global: com o contexto em "todas", a lista trazia
                                        // colaboradores de qualquer unidade mesmo depois de o
                                        // operador escolher uma filial para a venda. Mesmo
                                        // comportamento do formulário de agendamento.
                                        branch={formik.values.branch}
                                    />
                                </Col>
                            </Row>
                            <Row className="g-3 mt-0">
                                <Col md={12}>
                                    <FormLabel>{t('obs')}</FormLabel>
                                    <TextArea name="obs" style={{ height: "100px" }}
                                        maxLength={255}
                                        value={formik.values.obs}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        counter="true"
                                    />
                                </Col>
                            </Row>
                        </FormSection>

                        <FormSection
                            title={t('order_items')}
                            description={t('order_items_hint')}
                            icon="fa-solid fa-cart-shopping"
                            className="sales-order-form__items-section"
                        >

                            <div>
                                    <div className="sales-order-form__item-entry p-3 rounded mb-3">
                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <span className="sales-order-form__type-filter-label">
                                                    <i className="fas fa-filter" aria-hidden="true"></i>
                                                    {t('type')}
                                                </span>
                                                <ButtonGroup className="sales-order-form__type-filter" aria-label={t('type')}>
                                                    <Button
                                                        type="button"
                                                        variant={selectedProductType === null ? 'primary' : 'outline-primary'}
                                                        aria-pressed={selectedProductType === null}
                                                        onClick={() => onProductTypeChange(null)}
                                                    >
                                                        <i className="fa-solid fa-table-cells-large" aria-hidden="true" />
                                                        <span>{t('all')}</span>
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant={selectedProductType === 'PRODUCT' ? 'primary' : 'outline-primary'}
                                                        aria-pressed={selectedProductType === 'PRODUCT'}
                                                        onClick={() => onProductTypeChange('PRODUCT')}
                                                    >
                                                        <i className="fa-solid fa-box" aria-hidden="true" />
                                                        <span>{t('product')}</span>
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant={selectedProductType === 'SERVICE' ? 'primary' : 'outline-primary'}
                                                        aria-pressed={selectedProductType === 'SERVICE'}
                                                        onClick={() => onProductTypeChange('SERVICE')}
                                                    >
                                                        <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />
                                                        <span>{t('service')}</span>
                                                    </Button>
                                                </ButtonGroup>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3">
                                            {showBarcodeField && (
                                                <Col className="col-md-5">
                                                    <FormGroup>
                                                        <FormLabel className="fw-bold">
                                                            <i className="fas fa-barcode me-2"></i>
                                                            {t('barcode')}
                                                        </FormLabel>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder={t('scan_or_type_barcode')}
                                                            value={barcode}
                                                            onChange={(e) => setBarcode(e.target.value)}
                                                            onKeyPress={handleBarcodeKeyPress}
                                                            autoFocus
                                                        />
                                                        <small className="text-muted d-block mt-1">
                                                            <i className="fas fa-info-circle me-1"></i>
                                                            {t('press_enter_to_search')}
                                                        </small>
                                                    </FormGroup>
                                                </Col>
                                            )}
                                            <Col className={showBarcodeField ? 'col-md-7' : 'col-md-12'}>
                                                <FormGroup>
                                                    {/* O label acompanha o filtro de tipo: com SERVICE selecionado a busca
                                                        traz apenas serviços, e chamá-la de "Produto" contradizia o filtro
                                                        que o operador acabou de escolher. Sem filtro, "Todos" é o correto,
                                                        porque a lista mistura produtos e serviços. */}
                                                    <FormLabel className="fw-bold">
                                                        <i className="fas fa-search me-2"></i>
                                                        {selectedProductType === 'SERVICE'
                                                            ? t('service')
                                                            : selectedProductType === 'PRODUCT'
                                                                ? t('product')
                                                                : t('all')}
                                                    </FormLabel>
                                                    <ProductSelect
                                                        value={product}
                                                        type={selectedProductType}
                                                        onChange={(e) => {
                                                            if (e !== null) {
                                                                findProductDataById(e.value);
                                                                setBarcode(''); // Limpa o código de barras
                                                            } else {
                                                                setQty('1');
                                                                setPrice('0,00');
                                                                setUnit('UNIT');
                                                            }

                                                            setProduct(e);
                                                        }} />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3">
                                            <Col className="col-md-6">
                                                <FormGroup>
                                                    <FormLabel className="fw-bold" style={{whiteSpace: 'nowrap'}}>
                                                        <i className="fas fa-tag me-2"></i>
                                                        {t('price')} {(unit !== 'UNIT') ? '(Kg)' : '(Un)'}
                                                    </FormLabel>
                                                    <InputDecimal
                                                        name="price"
                                                        onValueChange={(e) => { setPrice(e.value) }}
                                                        value={price}
                                                        disabled={false}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col className="col-md-6">
                                                <FormGroup>
                                                    <FormLabel className="fw-bold">
                                                        <i className="fas fa-sort-numeric-up me-2"></i>
                                                        {t('qty')}
                                                    </FormLabel>
                                                    <QuantityInput
                                                        name="qty"
                                                        value={qty}
                                                        onChange={(e) => {
                                                            setQty(e.target.value);
                                                        }}
                                                        unit={unit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col className="col-md-12">
                                                <Button
                                                    disabled={disableAddButton}
                                                    variant="primary"
                                                    size="lg"
                                                    className="w-100"
                                                    style={{fontWeight: '600'}}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        onAdd();
                                                    }}>
                                                    <PlusIcon /> {t('add')} {t('item')}
                                                </Button>
                                            </Col>
                                        </Row>
                                        <div className="mt-3">
                                            <OrderStockNotice product={product} branch={formik.values.branch} />
                                        </div>
                                    </div>
                                    {items.length > 0 ? (
                                        <>
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <h5 className="mb-0">
                                                    <i className="fas fa-list me-2 text-primary"></i>
                                                    {t('items')} {t('added')}
                                                </h5>
                                                <span className="badge bg-primary" style={{fontSize: '0.9rem'}}>
                                                    {items.length}
                                                </span>
                                            </div>
                                            <Row>
                                                <Col className="col-md-12 table-responsive">
                                                    <table className="table sales-order-form__items-table">
                                                        <thead>
                                                            <tr>
                                                                <th className="fw-bold">
                                                                    <i className="fas fa-cube me-2"></i>
                                                                    {t('item')}
                                                                </th>
                                                                <th className="fw-bold text-center">
                                                                    <i className="fas fa-tag me-2"></i>
                                                                    {t('price')}
                                                                </th>
                                                                <th className="fw-bold text-center">
                                                                    <i className="fas fa-sort-numeric-up me-2"></i>
                                                                    {t('qty')}
                                                                </th>
                                                                <th className="fw-bold text-center">
                                                                    <i className="fas fa-calculator me-2"></i>
                                                                    {t('total')}
                                                                </th>
                                                                <th className="fw-bold text-center">
                                                                    <i className="fas fa-cog"></i>
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {
                                                                items.map((item) => {
                                                                    return (
                                                                        <tr key={item.product.id}>
                                                                            <td className="align-middle">
                                                                                <div className="d-flex align-items-center">
                                                                                    <ProductThumbnail
                                                                                        productId={item.product.id}
                                                                                        hasPhoto={!!item.product.uploadedFileLocation}
                                                                                        alt={item.product.name}
                                                                                    />
                                                                                    <span className="fw-bold">{item.product.name}</span>
                                                                                </div>
                                                                            </td>
                                                                            <td className="text-center align-middle">
                                                                                <span className="badge sales-order-form__price-badge" style={{fontSize: '0.9rem'}}>
                                                                                    {t('currency_symbol')} {convertDoubleToStringPTBR(item.price)}
                                                                                </span>
                                                                            </td>
                                                                            <td className="text-center align-middle">
                                                                                <span className="badge bg-secondary" style={{fontSize: '0.9rem'}}>
                                                                                    {convertQuantityToStringPTBR(item.qty)}
                                                                                </span>
                                                                            </td>
                                                                            <td className="text-center align-middle">
                                                                                <strong className="sales-order-form__item-total" style={{fontSize: '1.1rem'}}>
                                                                                    {t('currency_symbol')} {convertDoubleToStringPTBR(item.total)}
                                                                                </strong>
                                                                            </td>
                                                                            <td className="text-center align-middle">
                                                                                <button 
                                                                                    type="button" 
                                                                                    className="btn btn-danger btn-sm" 
                                                                                    title={t('msg_remove_item')} 
                                                                                    onClick={() => onRemove(item.product.id)}
                                                                                    style={{transition: 'all 0.3s'}}
                                                                                >
                                                                                    <i className="fa fa-trash"></i>
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })
                                                            }
                                                        </tbody>
                                                    </table>
                                                </Col>
                                            </Row>
                                        </>
                                    ) : (
                                        <div className="text-center py-5">
                                            <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
                                            <h5 className="text-muted">{t('no_items_added')}</h5>
                                            <p className="text-muted">{t('add_products_to_order')}</p>
                                        </div>
                                    )}
                            </div>
                        </FormSection>
                    </div>
                </Col>
                <Col className="col-md-4">
                    <aside className="card dlab-bg dlab-position sales-order-form__summary" style={{position: 'sticky', top: '20px'}}>
                        <div className="card-header border-0 pb-0">
                            <div className="d-flex align-items-center justify-content-between gap-2">
                                <h4 className="cate-title mb-0" style={{fontWeight: '600'}}>
                                    <i className="fas fa-shopping-cart me-2"></i>
                                    {t('order_summary')}
                                </h4>
                                {items.length > 0 && (
                                    <span className="badge sales-order-form__item-count">
                                        {items.length} {items.length === 1 ? t('item') : t('items')}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="card-body pt-3 pb-2">
                            {items.length > 0 && (
                                <div className="sales-order-form__summary-items mb-3">
                                    {items.map((item, index) => (
                                        <div
                                            key={`${item.product?.id ?? 'item'}-${index}`}
                                            className={`d-flex align-items-start justify-content-between gap-2 py-2 ${index < items.length - 1 ? 'border-bottom' : ''}`}
                                        >
                                            <div className="min-w-0">
                                                <span className="d-block text-truncate fw-medium">
                                                    {item.product?.name}
                                                </span>
                                                <small className="text-muted">
                                                    {item.qty} {item.qty === 1 ? t('item') : t('items')}
                                                </small>
                                            </div>
                                            <span className="text-nowrap fw-medium">
                                                {t('currency_symbol')} {convertDoubleToStringPTBR(item.total)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="card-footer pt-3 border-0" style={{borderRadius: '0 0 0.5rem 0.5rem'}}>
                            {
                                // Desconto da venda. Só aparece com item lançado, porque sem itens
                                // não há valor sobre o qual descontar.
                                items.length > 0 ?
                                    <div className="sales-order-form__discount mb-3">
                                        <div className="sales-order-form__discount-row">
                                            <span className="text-muted">{t('subtotal')}</span>
                                            <span>{t('currency_symbol')} {convertDoubleToStringPTBR(summaryTotal)}</span>
                                        </div>

                                        <FormLabel className="sales-order-form__discount-label mb-1">
                                            {t('discount')}
                                        </FormLabel>
                                        {/* Mesmo seletor das regras de comissão (SegmentedButtons): o tipo
                                            vem antes do número porque decide como ele é lido. */}
                                        <SegmentedButtons
                                            ariaLabel={t('discount')}
                                            value={formik.values.manualDiscountType}
                                            onChange={(value) => formik.setFieldValue('manualDiscountType', value)}
                                            options={[
                                                { value: OrderDiscountType.PERCENTAGE, label: t('order_discount_percentage'), icon: 'fa-solid fa-percent' },
                                                { value: OrderDiscountType.VALUE, label: t('order_discount_value'), icon: 'fa-solid fa-money-bill-wave' }
                                            ]}
                                        />
                                        <div className="mt-2">
                                            <InputDecimal
                                                id="manual-discount-input"
                                                name="manualDiscountInput"
                                                aria-label={t('discount')}
                                                value={formik.values.manualDiscountInput}
                                                // Opcional: o campo vazio é "sem desconto", e o placeholder diz isso.
                                                placeholder="0,00"
                                                // Venda toda coberta pelo plano não tem valor a receber para descontar.
                                                disabled={summaryTotal <= 0}
                                                onChange={(e) => formik.setFieldValue('manualDiscountInput', e.target.value)}
                                            />
                                        </div>
                                        {
                                            orderDiscount.error ?
                                                <small className="text-danger d-block mt-1">{t(orderDiscount.error)}</small>
                                                :
                                                <small className="text-muted d-block mt-1">{t('order_discount_hint')}</small>
                                        }

                                        {
                                            orderDiscount.value > 0 ?
                                                <div className="sales-order-form__discount-row sales-order-form__discount-row--applied mt-2">
                                                    <span>{t('discount')}</span>
                                                    <span>- {t('currency_symbol')} {convertDoubleToStringPTBR(orderDiscount.value)}</span>
                                                </div>
                                                :
                                                <></>
                                        }
                                    </div>
                                    :
                                    <></>
                            }
                            <div className="sales-order-form__final-total d-flex align-items-center justify-content-between gap-3 p-3 rounded">
                                <h4 className="font-w500 mb-0">
                                    <i className="fas fa-receipt me-2 text-primary"></i>
                                    {t('order_total')}
                                </h4>
                                <h2 className="font-w500 text-primary mb-0" style={{fontSize: '1.8rem'}}>
                                    {t('currency_symbol')} {convertDoubleToStringPTBR(orderNetTotal)}
                                </h2>
                            </div>
                        </div>
                    </aside>
                </Col>
            </Row>
        </>
    );
}

export default Form;
